import React, { useEffect, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import {
  incomeTypeRequiresPerson,
  isWageIncomeType,
  INCOME_TYPE_OPTIONS,
  type IncomeType,
} from "@perdiem/trpc/income-types";
import {
  capEmployeeRetirementContributions,
  estimatePayrollDeductionDefaults,
} from "@perdiem/trpc/wage-deductions";
import { Button } from "./Button";
import { Card } from "./Card";
import { CurrencyInput } from "./CurrencyInput";
import { FormErrorBanner } from "./FormErrorBanner";
import { Select } from "./Select";
import { TextInput } from "./TextInput";
import { formatCurrency } from "../lib/utils";
import { webPrivacyProps } from "../lib/privacy";

export type IncomePeriod = "annual" | "monthly";

/** Deduction values sent to the API. Dollar amounts are monthly; percentages are 0-100. */
export type WageDeductions = {
  federalTax: number;
  stateTax: number;
  socialSecurityTax: number;
  medicareTax: number;
  healthInsurance: number;
  dentalInsurance: number;
  visionInsurance: number;
  retirement401kPct: number;
  roth401kPct: number;
  hsaContribution: number;
  otherDeductions: number;
  employer401kMatchPct: number;
};

type DeductionSection = "pretax" | "taxes" | "aftertax" | "employer";

type DeductionFieldDef = {
  key: string;
  label: string;
  inputType: "currency" | "percent";
  section: DeductionSection;
  /** Key of a known tax-rate default that can auto-fill when the user focuses an empty field */
  smartDefault?: "socialSecurity" | "medicare";
};

const MIN_INCOME_YEAR = 2000;
const MAX_INCOME_YEAR = 2100;

const DEDUCTION_SECTION_LABELS: Record<DeductionSection, string> = {
  pretax: "Pre-tax Deductions",
  taxes: "Tax Withholdings",
  aftertax: "After-tax Deductions",
  employer: "Employer-paid (not deducted from pay)",
};

const DEDUCTION_FIELDS: DeductionFieldDef[] = [
  // Pre-tax deductions
  { key: "retirement401kPct", label: "Pre-tax Retirement Contributions, e.g. 401(k), TSP, FERS", inputType: "percent", section: "pretax" },
  { key: "healthInsurance", label: "Health Insurance", inputType: "currency", section: "pretax" },
  { key: "dentalInsurance", label: "Dental", inputType: "currency", section: "pretax" },
  { key: "visionInsurance", label: "Vision", inputType: "currency", section: "pretax" },
  { key: "hsaContribution", label: "HSA", inputType: "currency", section: "pretax" },
  // Tax withholdings
  { key: "federalTax", label: "Federal Tax", inputType: "currency", section: "taxes" },
  { key: "stateTax", label: "State Tax", inputType: "currency", section: "taxes" },
  { key: "socialSecurityTax", label: "Social Security / OASDI", inputType: "currency", section: "taxes", smartDefault: "socialSecurity" },
  { key: "medicareTax", label: "Medicare", inputType: "currency", section: "taxes", smartDefault: "medicare" },
  // After-tax deductions
  { key: "roth401kPct", label: "Tax Deferred Retirement Contributions, e.g. Roth 401(k), Roth TSP", inputType: "percent", section: "aftertax" },
  { key: "otherDeductions", label: "Other Deductions", inputType: "currency", section: "aftertax" },
  // Employer-paid
  { key: "employer401kMatchPct", label: "Employer Retirement Match", inputType: "percent", section: "employer" },
];

// FICA rates (2025)
const SS_RATE = 0.062;   // 6.2%
const MEDICARE_RATE = 0.0145; // 1.45%

/** Compute per-paycheck smart default from annual gross. */
function computeSmartDefault(
  kind: "socialSecurity" | "medicare",
  annualGross: number,
  payFreq: PayFrequency,
): number {
  const rate = kind === "socialSecurity" ? SS_RATE : MEDICARE_RATE;
  const annualAmount = annualGross * rate;
  const perPaycheck = annualAmount / PAY_FREQUENCY_MULTIPLIER[payFreq];
  return Math.round(perPaycheck * 100) / 100;
}

function smartDefaultPlaceholder(
  kind: "socialSecurity" | "medicare",
  annualGross: number,
  payFreq: PayFrequency,
): string {
  if (annualGross <= 0) return "0";
  const amt = computeSmartDefault(kind, annualGross, payFreq);
  const pctLabel = kind === "socialSecurity" ? "6.2%" : "1.45%";
  return `${formatCurrency(amt)} (${pctLabel})`;
}

const PERIOD_OPTIONS: { value: IncomePeriod; label: string }[] = [
  { value: "annual", label: "Annual" },
  { value: "monthly", label: "Monthly" },
];

export type PayFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly";

const PAY_FREQUENCY_OPTIONS: { value: PayFrequency; label: string }[] = [
  { value: "weekly", label: "Weekly (52/yr)" },
  { value: "biweekly", label: "Biweekly (26/yr)" },
  { value: "semimonthly", label: "Semi-monthly (24/yr)" },
  { value: "monthly", label: "Monthly (12/yr)" },
];

const PAY_FREQUENCY_MULTIPLIER: Record<PayFrequency, number> = {
  weekly: 52,
  biweekly: 26,
  semimonthly: 24,
  monthly: 12,
};

const PAY_FREQUENCY_LABELS: Record<PayFrequency, string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  semimonthly: "Semi-monthly",
  monthly: "Monthly",
};

function paychecksToMonthly(perPaycheck: number, freq: PayFrequency): number {
  return (perPaycheck * PAY_FREQUENCY_MULTIPLIER[freq]) / 12;
}

export function parseIncomeYear(value: string, label: "Begin" | "End"): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const year = parseInt(trimmed, 10);
  if (
    Number.isNaN(year) ||
    String(year) !== trimmed ||
    year < MIN_INCOME_YEAR ||
    year > MAX_INCOME_YEAR
  ) {
    throw new Error(`${label} year must be between ${MIN_INCOME_YEAR} and ${MAX_INCOME_YEAR}.`);
  }
  return year;
}

export function startDateForYear(year: number | null): string | null {
  return year === null ? null : `${year}-01-01`;
}

export function endDateAfterYear(year: number | null): string | null {
  return year === null ? null : `${year + 1}-01-01`;
}

export function lastActiveYearFromEndDate(value: string | null | undefined): string {
  if (!value) return "";
  const year = parseInt(value.slice(0, 4), 10);
  if (!Number.isFinite(year)) return "";
  return String(year - 1);
}

/** Get the effective per-paycheck value for a deduction field, falling back to smart default when empty. */
function effectiveDeductionValue(
  field: DeductionFieldDef,
  rawValue: string,
  annualGross: number,
  payFreq: PayFrequency,
): number {
  const val = parseFloat(rawValue);
  if (!Number.isNaN(val) && val > 0) return val;
  if (field.smartDefault && annualGross > 0) {
    return computeSmartDefault(field.smartDefault, annualGross, payFreq);
  }
  return 0;
}

/** Compute per-paycheck dollar amount from a percentage of annual gross. */
function pctToPerPaycheck(pct: number, annualGross: number, payFreq: PayFrequency): number {
  return (pct / 100) * annualGross / PAY_FREQUENCY_MULTIPLIER[payFreq];
}

function monthlyToPaycheck(monthly: number, freq: PayFrequency): number {
  return (monthly * 12) / PAY_FREQUENCY_MULTIPLIER[freq];
}

function deductionInputTestId(key: string): string {
  return `income-form-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}-input`;
}

type IncomeStreamFormProfile = {
  id: string;
  type: "primary" | "spouse";
  firstName: string | null;
  lastName: string | null;
  state?: string | null;
  filingStatus?: string | null;
};

type IncomeStreamFormProps = {
  profiles?: IncomeStreamFormProfile[];
  onAddIncome: (input: {
    name: string;
    amount: number;
    period: IncomePeriod;
    monthlyAmount: number;
    type: IncomeType;
    personId: string | null;
    growthRate: number;
    begin: string | null;
    end: string | null;
    deductions?: WageDeductions;
    payFrequency?: PayFrequency;
  }) => Promise<void>;
  loading?: boolean;
  addButtonLabel?: string;
  cardTitle?: string;
  /** Start with the form already visible */
  initiallyOpen?: boolean;
  /** Called when the user cancels (for externally-controlled visibility) */
  onCancel?: () => void;
};

function getProfileLabel(
  profile: IncomeStreamFormProfile,
  totalProfiles: number,
) {
  const name = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name) return name;
  if (totalProfiles === 1) return "You";
  return profile.type === "primary" ? "Primary" : "Spouse";
}

const AUTO_PAYROLL_DEDUCTION_KEYS = [
  "federalTax",
  "stateTax",
  "socialSecurityTax",
  "medicareTax",
] as const;

function formatDeductionInput(value: number | undefined): string {
  if (!value || value <= 0) return "";
  return String(Math.round(value * 100) / 100);
}

function TakeHomeSummary({
  grossAmount,
  period,
  deductionStrings,
  payFrequency,
}: {
  grossAmount: string;
  period: IncomePeriod;
  deductionStrings: Record<string, string>;
  payFrequency: PayFrequency;
}) {
  const gross = parseFloat(grossAmount) || 0;
  if (gross <= 0) return null;

  const annualGross = period === "annual" ? gross : gross * 12;
  const currentYear = new Date().getFullYear();
  const retirementPct = parseFloat(deductionStrings.retirement401kPct) || 0;
  const rothPct = parseFloat(deductionStrings.roth401kPct) || 0;
  const cappedRetirement = capEmployeeRetirementContributions(
    (retirementPct / 100) * annualGross,
    (rothPct / 100) * annualGross,
    currentYear,
  );

  // Sum annual deductions: currency fields are per-paycheck, % fields are of gross
  // Uses smart defaults for SS/Medicare when those fields are empty
  const annualDeductions = DEDUCTION_FIELDS
    .filter((f) => f.key !== "employer401kMatchPct")
    .reduce((sum, field) => {
      if (field.inputType === "percent") {
        if (field.key === "retirement401kPct") return sum + cappedRetirement.employeePretax;
        if (field.key === "roth401kPct") return sum + cappedRetirement.employeeRoth;
      }
      const val = effectiveDeductionValue(field, deductionStrings[field.key], annualGross, payFrequency);
      return sum + val * PAY_FREQUENCY_MULTIPLIER[payFrequency];
    }, 0);

  const annualTakeHome = annualGross - annualDeductions;
  const multiplier = PAY_FREQUENCY_MULTIPLIER[payFrequency];
  const grossPerPaycheck = annualGross / multiplier;
  const netPerPaycheck = annualTakeHome / multiplier;
  const freqLabel = PAY_FREQUENCY_LABELS[payFrequency];

  return (
    <View className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 gap-1">
      <View className="flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Gross Per Paycheck ({freqLabel})
        </Text>
        <Text className="text-xs font-medium text-gray-700 dark:text-gray-300" {...webPrivacyProps('financial')}>
          {formatCurrency(grossPerPaycheck)}
        </Text>
      </View>
      <View className="flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Deductions
        </Text>
        <Text className="text-xs font-medium text-red-600 dark:text-red-400" {...webPrivacyProps('financial')}>
          −{formatCurrency(annualDeductions / multiplier)}
        </Text>
      </View>
      <View className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1 flex-row justify-between">
        <Text className="text-xs font-semibold text-green-600 dark:text-green-400">
          Net Per Paycheck
        </Text>
        <Text className="text-xs font-semibold text-green-600 dark:text-green-400" {...webPrivacyProps('financial')}>
          {formatCurrency(netPerPaycheck)}
        </Text>
      </View>
      <View className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1 flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Gross Annual
        </Text>
        <Text className="text-xs font-medium text-gray-700 dark:text-gray-300" {...webPrivacyProps('financial')}>
          {formatCurrency(annualGross)}
        </Text>
      </View>
      <View className="flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Take-Home Annual
        </Text>
        <Text className="text-xs font-medium text-green-600 dark:text-green-400" {...webPrivacyProps('financial')}>
          {formatCurrency(annualTakeHome)}
        </Text>
      </View>
    </View>
  );
}

export function IncomeStreamForm({
  profiles = [],
  onAddIncome,
  loading = false,
  addButtonLabel = "+ Add income manually",
  cardTitle = "Add Income Stream",
  initiallyOpen = false,
  onCancel,
}: IncomeStreamFormProps) {
  const visibleProfiles = profiles.filter(
    (profile) => profile.type === "primary" || profile.type === "spouse",
  );
  const defaultWagePersonId =
    visibleProfiles.length === 1 ? visibleProfiles[0]!.id : null;
  const personOptions = visibleProfiles.map((profile) => ({
    value: profile.id,
    label: getProfileLabel(profile, visibleProfiles.length),
  }));

  const [addingManual, setAddingManual] = useState(initiallyOpen);
  const [incomeName, setIncomeName] = useState("");
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState<IncomePeriod>("annual");
  const [incomeType, setIncomeType] = useState<IncomeType>("wages");
  const [personId, setPersonId] = useState<string | null>(defaultWagePersonId);
  const [growthRate, setGrowthRate] = useState("3.0");
  const [beginYear, setBeginYear] = useState("");
  const [endYear, setEndYear] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deductionStrings, setDeductionStrings] = useState<Record<string, string>>(
    () => Object.fromEntries(DEDUCTION_FIELDS.map(({ key }) => [key, ""])),
  );
  const [touchedDeductionKeys, setTouchedDeductionKeys] = useState<Record<string, boolean>>({});
  const [showDeductions, setShowDeductions] = useState(true);
  const [payFrequency, setPayFrequency] = useState<PayFrequency>("biweekly");
  const annualGross =
    period === "annual"
      ? parseFloat(amount) || 0
      : (parseFloat(amount) || 0) * 12;
  const primaryProfile =
    visibleProfiles.find((profile) => profile.type === "primary") ??
    visibleProfiles[0] ??
    null;
  const selectedWageProfile =
    visibleProfiles.find((profile) => profile.id === personId) ??
    primaryProfile;
  const payrollDefaults = useMemo(() => {
    if (!isWageIncomeType(incomeType) || annualGross <= 0) return null;
    return estimatePayrollDeductionDefaults({
      annualGross,
      payFrequency,
      state: selectedWageProfile?.state ?? primaryProfile?.state ?? null,
      filingStatus:
        selectedWageProfile?.filingStatus ??
        primaryProfile?.filingStatus ??
        null,
    }).perPaycheck;
  }, [
    annualGross,
    incomeType,
    payFrequency,
    primaryProfile?.filingStatus,
    primaryProfile?.state,
    selectedWageProfile?.filingStatus,
    selectedWageProfile?.state,
  ]);

  useEffect(() => {
    if (!addingManual) return;
    if (!incomeTypeRequiresPerson(incomeType)) return;
    if (personId || !defaultWagePersonId) return;
    setPersonId(defaultWagePersonId);
  }, [addingManual, defaultWagePersonId, incomeType, personId]);

  const reset = () => {
    setAddingManual(false);
    setIncomeName("");
    setAmount("");
    setPeriod("annual");
    setIncomeType("wages");
    setPersonId(defaultWagePersonId);
    setGrowthRate("3.0");
    setBeginYear("");
    setEndYear("");
    setError(null);
    setDeductionStrings(
      Object.fromEntries(DEDUCTION_FIELDS.map(({ key }) => [key, ""])),
    );
    setTouchedDeductionKeys({});
    setShowDeductions(true);
    setPayFrequency("biweekly");
    onCancel?.();
  };

  useEffect(() => {
    if (!addingManual || !isWageIncomeType(incomeType) || !payrollDefaults) {
      return;
    }

    setDeductionStrings((current) => {
      let changed = false;
      const next = { ...current };

      for (const key of AUTO_PAYROLL_DEDUCTION_KEYS) {
        if (touchedDeductionKeys[key]) continue;
        const value = formatDeductionInput(payrollDefaults[key]);
        if (next[key] !== value) {
          next[key] = value;
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [
    addingManual,
    incomeType,
    payrollDefaults,
    touchedDeductionKeys,
  ]);

  const handleAdd = async () => {
    setError(null);
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setError("Please enter a valid income amount.");
      return;
    }

    const trimmedName = incomeName.trim();
    if (!trimmedName) {
      setError("Please enter a name for this income stream.");
      return;
    }

    if (incomeTypeRequiresPerson(incomeType) && !personId) {
      setError(
        "Assign wage income to a person so FIRE simulations stop it correctly.",
      );
      return;
    }

    const parsedGrowthRate = parseFloat(growthRate);
    if (Number.isNaN(parsedGrowthRate) || parsedGrowthRate < 0 || parsedGrowthRate > 99) {
      setError("Annual growth rate must be between 0% and 99%.");
      return;
    }

    let beginDate: string | null = null;
    let endDate: string | null = null;
    try {
      const parsedBeginYear = parseIncomeYear(beginYear, "Begin");
      const parsedEndYear = parseIncomeYear(endYear, "End");
      if (
        parsedBeginYear !== null &&
        parsedEndYear !== null &&
        parsedEndYear < parsedBeginYear
      ) {
        setError("End year must be the same as or after begin year.");
        return;
      }
      beginDate = startDateForYear(parsedBeginYear);
      endDate = endDateAfterYear(parsedEndYear);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enter valid income years.");
      return;
    }

    // Parse deductions for wage income
    let deductions: WageDeductions | undefined;
    if (isWageIncomeType(incomeType)) {
      const parsed: Record<string, number> = {};
      for (const { key, label, inputType } of DEDUCTION_FIELDS) {
        const raw = deductionStrings[key].trim();
        if (raw) {
          const val = parseFloat(raw);
          if (Number.isNaN(val) || val < 0) {
            setError(`Invalid value for ${label}.`);
            return;
          }
          if (inputType === "percent") {
            parsed[key] = val; // Store as-is (percentage)
          } else {
            // Per-paycheck → monthly
            parsed[key] = paychecksToMonthly(val, payFrequency);
          }
        } else if (
          payrollDefaults &&
          AUTO_PAYROLL_DEDUCTION_KEYS.includes(key as (typeof AUTO_PAYROLL_DEDUCTION_KEYS)[number]) &&
          !touchedDeductionKeys[key]
        ) {
          parsed[key] = paychecksToMonthly(
            payrollDefaults[key as keyof typeof payrollDefaults] ?? 0,
            payFrequency,
          );
        } else {
          parsed[key] = 0;
        }
      }
      deductions = parsed as unknown as WageDeductions;
    }

    const monthlyAmount =
      period === "annual" ? parsedAmount / 12 : parsedAmount;
    try {
      await onAddIncome({
        name: trimmedName,
        amount: parsedAmount,
        period,
        monthlyAmount,
        type: incomeType,
        personId: incomeTypeRequiresPerson(incomeType) ? personId : null,
        growthRate: parsedGrowthRate / 100,
        begin: beginDate,
        end: endDate,
        deductions,
        payFrequency: isWageIncomeType(incomeType) ? payFrequency : undefined,
      });
      reset();
    } catch {
      setError("Could not add income stream. Please try again.");
    }
  };

  if (!addingManual) {
    return (
      <Button
        testID="income-form-open-button"
        accessibilityLabel="Add income manually"
        variant="ghost"
        onPress={() => setAddingManual(true)}
      >
        {addButtonLabel}
      </Button>
    );
  }

  return (
    <Card>
      <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        {cardTitle}
      </Text>

      <FormErrorBanner error={error} onDismiss={() => setError(null)} />
      {error && <View className="mb-3" />}

      <TextInput
        testID="income-form-name-input"
        accessibilityLabel="Income name"
        label="Name"
        value={incomeName}
        onChangeText={(text) => {
          setIncomeName(text);
          setError(null);
        }}
        placeholder="e.g. Salary, Freelance, Rental Income"
        containerClassName="mb-4"
      />

      <Select
        label="Income type"
        value={incomeType}
        options={INCOME_TYPE_OPTIONS}
        onChange={(value) => {
          setIncomeType(value);
          setError(null);
          if (incomeTypeRequiresPerson(value) && defaultWagePersonId) {
            setPersonId((current) => current ?? defaultWagePersonId);
          }
        }}
        className="mb-4"
      />

      {incomeTypeRequiresPerson(incomeType) && (
        <Select
          label="Person"
          value={personId}
          options={personOptions}
          onChange={(value) => {
            setPersonId(value);
            setError(null);
          }}
          placeholder="Select the person earning this wage income"
          className="mb-4"
          error={
            error && !personId
              ? "Wage income must be assigned to a person."
              : null
          }
        />
      )}

      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {incomeTypeRequiresPerson(incomeType)
          ? "Gross / pre-tax amount & period"
          : "Amount & period"}
      </Text>
      <View className="flex-row gap-2 mb-4 items-center">
        <CurrencyInput
          testID="income-form-amount-input"
          accessibilityLabel="Income amount"
          value={amount}
          onChangeText={(text) => {
            setAmount(text);
            setError(null);
          }}
          placeholder="e.g. 85,000"
          containerClassName="flex-1"
        />
        <View className="flex-row gap-1">
          {PERIOD_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant={period === option.value ? "primary" : "secondary"}
              size="sm"
              onPress={() => setPeriod(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </View>
      </View>

      {isWageIncomeType(incomeType) && (
        <View className="mb-4">
          <Pressable
            testID="income-form-deductions-toggle"
            accessibilityLabel={showDeductions ? 'Hide payroll deductions' : 'Add payroll deductions'}
            onPress={() => setShowDeductions(!showDeductions)}
            className="flex-row items-center justify-between py-2"
          >
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Payroll Deductions
            </Text>
            <Text className="text-xs text-green-600 dark:text-green-400">
              {showDeductions ? "Hide" : "Add deductions"}
            </Text>
          </Pressable>
          {showDeductions && (
            <View className="gap-3 mt-1">
              <Select
                label="Pay frequency"
                value={payFrequency}
                options={PAY_FREQUENCY_OPTIONS}
                onChange={(v) => setPayFrequency(v)}
                size="sm"
              />
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                Estimated per-paycheck tax defaults are filled in. Replace them
                with your pay stub amounts when you have them.
              </Text>
              {DEDUCTION_FIELDS.map(({ key, label, inputType, smartDefault, section }, i) => {
                const prevSection = i > 0 ? DEDUCTION_FIELDS[i - 1]!.section : null;
                const sectionHeader = section !== prevSection ? (
                  <Text key={`section-${section}`} className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-2">
                    {DEDUCTION_SECTION_LABELS[section]}
                  </Text>
                ) : null;

                const annualGross = period === "annual"
                  ? (parseFloat(amount) || 0)
                  : (parseFloat(amount) || 0) * 12;

                if (inputType === "percent") {
                  const pctVal = parseFloat(deductionStrings[key]) || 0;
                  const dollarAmt = pctVal > 0 && annualGross > 0
                    ? pctToPerPaycheck(pctVal, annualGross, payFrequency)
                    : 0;
                  return (
                    <React.Fragment key={key}>
                      {sectionHeader}
                      <View className="flex-row items-end gap-2">
                        <TextInput
                          testID={deductionInputTestId(key)}
                          accessibilityLabel={label}
                          label={`${label} (%)`}
                          value={deductionStrings[key]}
                          onChangeText={(text) => {
                            setTouchedDeductionKeys((prev) => ({ ...prev, [key]: true }));
                            setDeductionStrings((prev) => ({ ...prev, [key]: text }));
                            setError(null);
                          }}
                          placeholder="0"
                          keyboardType="decimal-pad"
                          size="sm"
                          containerClassName="flex-1"
                        />
                        {dollarAmt > 0 && (
                          <Text
                            className="text-xs text-gray-400 dark:text-gray-500 pb-2.5"
                            {...webPrivacyProps('financial')}
                          >
                            = {formatCurrency(dollarAmt)}/pay
                          </Text>
                        )}
                      </View>
                    </React.Fragment>
                  );
                }
                // SS/Medicare: show computed default, auto-fill on focus
                const defaultVal = payrollDefaults?.[key as keyof typeof payrollDefaults] ??
                  (smartDefault && annualGross > 0
                    ? computeSmartDefault(smartDefault, annualGross, payFrequency)
                    : 0);
                const placeholder = smartDefault && defaultVal > 0
                  ? `${formatCurrency(defaultVal)} (${smartDefault === "socialSecurity" ? "6.2" : "1.45"}%)`
                  : "0";
                return (
                  <React.Fragment key={key}>
                    {sectionHeader}
                    <CurrencyInput
                      testID={deductionInputTestId(key)}
                      accessibilityLabel={label}
                      label={`${label} (per paycheck)`}
                      value={deductionStrings[key]}
                      onChangeText={(text) => {
                        setTouchedDeductionKeys((prev) => ({ ...prev, [key]: true }));
                        setDeductionStrings((prev) => ({ ...prev, [key]: text }));
                        setError(null);
                      }}
                      onFocus={
                        smartDefault && !deductionStrings[key] && !touchedDeductionKeys[key]
                          ? () => {
                              if (defaultVal > 0) {
                                setDeductionStrings((prev) => ({
                                  ...prev,
                                  [key]: String(defaultVal),
                                }));
                              }
                            }
                          : undefined
                      }
                      placeholder={placeholder}
                      size="sm"
                    />
                  </React.Fragment>
                );
              })}
              <TakeHomeSummary
                grossAmount={amount}
                period={period}
                deductionStrings={deductionStrings}
                payFrequency={payFrequency}
              />
            </View>
          )}
        </View>
      )}

      <View className="gap-3 mb-4">
        <TextInput
          testID="income-form-growth-rate-input"
          accessibilityLabel="Income annual growth rate"
          label="Annual growth rate (%)"
          value={growthRate}
          onChangeText={(text) => {
            setGrowthRate(text);
            setError(null);
          }}
          placeholder="e.g. 3"
          keyboardType="decimal-pad"
        />
        <View className="flex-row gap-2">
          <TextInput
            testID="income-form-begin-year-input"
            accessibilityLabel="Income begin year"
            label="Begin year (optional)"
            value={beginYear}
            onChangeText={(text) => {
              setBeginYear(text);
              setError(null);
            }}
            placeholder="e.g. 2028"
            keyboardType="number-pad"
            containerClassName="flex-1"
          />
          <TextInput
            testID="income-form-end-year-input"
            accessibilityLabel="Income end year"
            label="End year (optional)"
            value={endYear}
            onChangeText={(text) => {
              setEndYear(text);
              setError(null);
            }}
            placeholder="e.g. 2030"
            keyboardType="number-pad"
            containerClassName="flex-1"
          />
        </View>
      </View>

      <View className="flex-row gap-2">
        <Button
          testID="income-form-add-button"
          accessibilityLabel="Save income stream"
          className="flex-1"
          onPress={handleAdd}
          loading={loading}
        >
          Add
        </Button>
        <Button
          testID="income-form-cancel-button"
          accessibilityLabel="Cancel income entry"
          variant="ghost"
          onPress={reset}
        >
          Cancel
        </Button>
      </View>
    </Card>
  );
}
